import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useUser } from '@clerk/clerk-react';
import axios from 'axios';
import {
  selectedClientSelector,
  setSelectedClient
} from '../../store/slices/selectedClient';
import { clientsSelector, getClients } from '../../store/slices/clients';
import { authSelector } from '../../store/slices/auth';
import { API_KEY, API_CLIENTS_NAME } from '../../utils/constants';
import ReactSelect from 'react-select';
import {
  Search,
  Globe,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  Circle,
  Triangle,
  Square,
  Download,
  RefreshCw,
  ExternalLink,
  Shield,
  Zap,
  FileText,
  Monitor,
  Eye,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Code,
  FileSearch,
  AlertCircle,
  Info,
  Settings
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Link } from 'react-router-dom';

import WebsiteSEOAuditSkeleton from './Skeleton';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import SEOReportTemplate from './SEOReportTemplate';
import {
  transformResults,
  getFailedChecks,
  getPassedChecks
} from '../../utils/seoAuditHelpers';

const API_BASE =
  process.env.REACT_APP_SEO_AUDIT_API ||
  'https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getStatusIcon = (status) => {
  switch (status) {
    case 'good':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'warning':
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    case 'critical':
      return <XCircle className="h-4 w-4 text-red-500" />;
    default:
      return null;
  }
};

const getCategoryIcon = (name) => {
  switch (name) {
    case 'Technical':
      return <Monitor className="h-4 w-4" />;
    case 'On-Page SEO':
      return <FileText className="h-4 w-4" />;
    case 'Performance':
      return <Zap className="h-4 w-4" />;
    case 'Security':
      return <Shield className="h-4 w-4" />;
    case 'Accessibility':
      return <Eye className="h-4 w-4" />;
    default:
      return <Globe className="h-4 w-4" />;
  }
};

const getPriorityIcon = (priority) => {
  switch (priority) {
    case 'critical':
    case 'high':
      return <Circle className="h-4 w-4 text-red-500" />;
    case 'medium':
      return <Triangle className="h-4 w-4 text-yellow-500" />;
    case 'low':
      return <Square className="h-4 w-4 text-blue-500" />;
    default:
      return null;
  }
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'critical':
    case 'high':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'medium':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    case 'low':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getPriorityLabel = (priority) => {
  switch (priority) {
    case 'critical':
      return 'Critical';
    case 'high':
      return 'High Priority';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
    default:
      return priority;
  }
};

const getScoreColor = (score) => {
  if (score >= 80) return 'text-green-600';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-600';
};

const getScoreBorderColor = (score) => {
  if (score >= 80) return 'border-green-500';
  if (score >= 50) return 'border-yellow-500';
  return 'border-red-500';
};

// Hardcoded "Why it matters?" messages for recommendation types
const WHY_IT_MATTERS = {
  'Title Too Short':
    'Short titles miss valuable keywords and fail to fully describe your page, reducing click-through rates in search results.',
  'Title Too Long':
    'Titles over 60 characters get truncated in search results, potentially hiding important keywords from users.',
  'Missing Page Title':
    'Pages without titles cannot be properly indexed or displayed in search results, severely hurting visibility.',
  'Missing Meta Description':
    'Without a meta description, search engines auto-generate snippets that may not represent your page well.',
  'Missing Image Alt Text':
    'Alt text improves accessibility for visually impaired users and helps search engines understand image content.',
  'Missing Image Titles':
    'Image title attributes provide additional context on hover and can contribute to image search rankings.',
  'Render Blocking Resources':
    'Render-blocking CSS and JS delay how quickly users see your page, increasing bounce rates.',
  'Low Content Rate':
    'Pages with a low text-to-HTML ratio may be seen as thin content by search engines, weakening rankings.',
  'High Content Rate':
    'An unusually high text ratio can indicate poor HTML structure which affects parsing by crawlers.',
  'Low Character Count':
    'Very short pages may not provide enough value to rank competitively for target keywords.',
  'High Character Count':
    'Excessively long pages without clear structure can overwhelm users and reduce engagement.',
  'Low Readability':
    'Hard-to-read content reduces time on page and engagement, indirectly affecting search rankings.',
  'Non-SEO Friendly URL':
    'Clean, descriptive URLs improve click-through rates and help search engines understand page topics.',
  'Missing H1 Tag':
    'The H1 tag is the primary heading signal for search engines to understand what a page is about.',
  'Broken Internal Links':
    'Broken links waste crawl budget, hurt user experience, and pass zero link equity.',
  'Missing Sitemap':
    'Without a sitemap, search engines may miss important pages on your site during crawling.',
  'Missing robots.txt':
    'Robots.txt controls which pages crawlers access — without it, sensitive pages may be indexed.',
  'HTTPS Not Enabled':
    'HTTPS is a confirmed ranking factor and essential for user trust and data security.',
  'Redirect Loops':
    'Redirect loops prevent pages from loading entirely, making them inaccessible to users and crawlers.',
  'Non-Indexable Pages':
    'If important pages are blocked from indexing, they cannot appear in search results at all.',
  'Duplicate Title Tags':
    'Duplicate titles confuse search engines about which page to rank for a given query.',
  'Duplicate Meta Descriptions':
    'Duplicate descriptions reduce the uniqueness signal and may lead to poor snippet selection.',
  'Duplicate Content':
    'Duplicate content splits ranking signals between pages, diluting their individual authority.',
  'Broken Resources':
    'Missing CSS, JS, or images cause visual and functional issues that increase bounce rates.',
  'Server Signature Exposed':
    'Exposing server version information makes it easier for attackers to target known vulnerabilities.',
  'Schema Markup Errors':
    'Structured data errors prevent rich snippets from appearing, reducing visibility in search results.',
  'Missing Doctype':
    'A missing doctype forces browsers into quirks mode, causing inconsistent rendering across devices.',
  'Missing Encoding Meta Tag':
    'Without character encoding, browsers may misinterpret special characters, breaking content display.',
  'Mixed Content':
    'Mixed HTTP/HTTPS content triggers browser security warnings that scare away visitors.',
  'Page Size Too Large':
    'Large page sizes slow loading times, especially on mobile, hurting Core Web Vitals scores.',
  'Meta Refresh Redirect':
    'Meta refresh redirects are slower than server-side redirects and can confuse search engines.',
  'Deprecated HTML Tags':
    'Deprecated tags may not render correctly in modern browsers and signal outdated code.',
  'Duplicate Meta Tags':
    'Multiple identical meta tags confuse crawlers and may cause unpredictable indexing behavior.',
  'Duplicate Title':
    'Pages sharing the same title compete against each other in search results.',
  'Missing Favicon':
    'A favicon improves brand recognition in browser tabs and bookmarks, increasing return visits.',
  'Flash Content':
    'Flash is unsupported by modern browsers and search engines, making content invisible.',
  'Lorem Ipsum Detected':
    'Placeholder text signals an unfinished page, which can hurt credibility and rankings.',
  'Recursive Canonical':
    'Incorrect self-referencing canonicals can create crawl loops and waste crawl budget.',
  'Canonical Chain':
    'Chained canonicals dilute the signal and may be ignored by search engines.',
  'Canonical to Broken Page':
    "Pointing a canonical to a broken URL wastes the page's ranking potential entirely.",
  'Links to Redirects':
    'Internal links to redirecting URLs add latency and waste crawl budget on indirect paths.',
  'Orphan Page':
    'Pages with no internal links are hard for search engines to discover and rarely rank well.',
  'Redirect Chain':
    'Each extra redirect adds load time and risks the chain being truncated by crawlers.',
  'Irrelevant Description':
    "A meta description that doesn't match content leads to poor click-through and higher bounce rates.",
  'Irrelevant Title':
    'A misleading title causes users to leave quickly, sending negative engagement signals.',
  'Irrelevant Meta Keywords':
    'While meta keywords are largely ignored, irrelevant ones can signal low-quality optimization.',
  'Frame/IFrame Content':
    'Content within frames is harder for search engines to index and may not pass link equity.'
};

// ─── Component ───────────────────────────────────────────────────────────────

const WebsiteSEOAudit = () => {
  const dispatch = useDispatch();
  const { user } = useUser();
  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);
  const { userInfo } = useSelector(authSelector);

  // Determine if user is superAdmin based on domain
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  const [isScanning, setIsScanning] = useState(false);
  const [scanPhase, setScanPhase] = useState(''); // 'starting' | 'crawling' | 'analyzing' | ''
  const [pagesCrawled, setPagesCount] = useState(0);
  const [auditResults, setAuditResults] = useState(null);
  const [error, setError] = useState('');
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [customUrl, setCustomUrl] = useState('');
  const [maxPages, setMaxPages] = useState(1);
  const [showRawData, setShowRawData] = useState(false);
  const [healthChecksOpen, setHealthChecksOpen] = useState(false);
  const [expandedPages, setExpandedPages] = useState({});
  const [expandedRecommendations, setExpandedRecommendations] = useState({});
  const [psiStrategy, setPsiStrategy] = useState('mobile'); // 'mobile' | 'desktop'
  const [severityFilter, setSeverityFilter] = useState('all'); // 'all' | 'critical' | 'high' | 'medium' | 'low'
  const reportRef = useRef(null);

  const handleDownloadReport = async () => {
    if (!auditResults) return;

    // Show loading or toast
    console.log('Generating Request...');

    try {
      const input = reportRef.current;
      if (!input) {
        throw new Error('Report element not found');
      }

      // Wait a moment for any renders
      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: 1200
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      const pageHeight = 297;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      let hostname = 'website';
      try {
        const urlToUse =
          auditResults.url || websiteUrl || 'https://example.com';
        // Ensure protocol is present
        const safeUrl = urlToUse.startsWith('http')
          ? urlToUse
          : `https://${urlToUse}`;
        hostname = new URL(safeUrl).hostname;
      } catch (e) {
        console.warn('Could not parse hostname for PDF filename:', e);
      }

      pdf.save(`SEO-Audit-Report-${hostname}.pdf`);

      console.log('Report downloaded successfully.');
    } catch (error) {
      console.error('PDF Generation failed:', error);
      alert(`Failed to generate PDF report: ${error.message}`);
    }
  };

  const pollingRef = useRef(null);

  // Handle change in client search select
  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;

    try {
      // Fetch complete client data from API to ensure all attributes are present
      const response = await axios.get(
        `${API_CLIENTS_NAME}${selectedOption.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );

      if (response?.data) {
        // Update selected client in Redux with complete data including socialnetworks
        dispatch(setSelectedClient(response.data));
      } else {
        // Fallback to using the client from customerList if API call returns no data
        dispatch(setSelectedClient(selectedOption));
      }
    } catch (error) {
      console.error('Error fetching complete client data:', error);
      // Fallback to using the client from customerList if API call fails
      dispatch(setSelectedClient(selectedOption));
    }

    // Force full page reload to ensure all components refresh with new client data
    window.location.href = '/seo-audit';
  };

  // Set label and value for search
  const getCustomerList = () => {
    if (!customerList?.length) {
      dispatch(getClients());
    }

    return customerList.map((customer) => ({
      ...customer,
      label: customer.clientName,
      value: customer.clientName
    }));
  };

  // Get website URL from client profile
  const websiteUrl = selectedClient?.mergeFields?.['~Website'] || '';

  // Initial loading delay (0.5 seconds)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Initialize custom URL with client's website URL when client changes
  useEffect(() => {
    if (selectedClient?.mergeFields?.['~Website']) {
      setCustomUrl(selectedClient.mergeFields['~Website']);
    }
  }, [selectedClient]);

  // Load cached audit results from DynamoDB if available
  useEffect(() => {
    const loadCachedResults = async () => {
      try {
        const cachedUrl = customUrl || websiteUrl;
        if (!cachedUrl) return;

        const cacheKey = `seo_audit_${cachedUrl.replace(/[^a-zA-Z0-9]/g, '_')}`;
        const cached = localStorage.getItem(cacheKey);
        if (!cached) return;

        const { reportId, savedAt } = JSON.parse(cached);
        if (!reportId) return;

        // Only use cache if less than 24 hours old
        const age = Date.now() - new Date(savedAt).getTime();
        if (age > 24 * 60 * 60 * 1000) {
          localStorage.removeItem(cacheKey);
          return;
        }

        console.log('[SEO Audit] Loading cached report:', reportId);
        const res = await fetch(`${API_BASE}/audit/report/${reportId}`);
        if (!res.ok) return;

        const data = await res.json();
        const results = data.results || data;
        const transformed = transformResults(results);
        setAuditResults(transformed);
        console.log('[SEO Audit] Loaded cached results successfully');
      } catch (err) {
        console.warn('[SEO Audit] Failed to load cached results:', err.message);
      }
    };
    loadCachedResults();
  }, [customUrl, websiteUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Validate URL format - accepts URLs with or without protocol
  const isValidUrl = (string) => {
    if (!string || typeof string !== 'string') return false;
    const trimmed = string.trim();
    if (!trimmed) return false;
    try {
      // Accept URLs with or without protocol
      const urlWithProtocol = trimmed.startsWith('http')
        ? trimmed
        : `https://${trimmed}`;
      new URL(urlWithProtocol);
      return true;
    } catch (_) {
      return false;
    }
  };

  // ── API calls ──

  const startAudit = useCallback(async () => {
    const urlToAudit = customUrl.trim();

    if (!urlToAudit) {
      setError('Please enter a website URL to audit.');
      return;
    }

    if (!isValidUrl(urlToAudit)) {
      setError(
        'Please enter a valid website URL (e.g., example.com or https://example.com)'
      );
      return;
    }

    setIsScanning(true);
    setScanPhase('starting');
    setError('');
    setAuditResults(null);
    setPagesCount(0);

    try {
      // 1. Start the audit
      const startRes = await fetch(`${API_BASE}/audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: urlToAudit, maxPages })
      });

      if (!startRes.ok) {
        const errData = await startRes.json().catch(() => ({}));
        throw new Error(
          errData.error || `Failed to start audit (${startRes.status})`
        );
      }

      const startData = await startRes.json();
      const taskId = startData.taskId;

      if (!taskId) throw new Error('No taskId returned from audit start');

      console.log('[SEO Audit] Started, taskId:', taskId);
      setScanPhase('crawling');

      let errorCount = 0;
      // 2. Poll status
      pollingRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API_BASE}/audit/${taskId}/status`);

          if (!statusRes.ok) {
            throw new Error(`HTTP error ${statusRes.status}`);
          }

          const statusData = await statusRes.json();
          errorCount = 0; // reset on success

          console.log('[SEO Audit] Status:', statusData);
          setPagesCount(statusData.pages_crawled || 0);

          if (statusData.status === 'completed') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setScanPhase('analyzing');

            // 3. Fetch results with a retry mechanism
            let resultsRes;
            let resultsData;
            let resultsErrorCount = 0;
            const maxResultsRetries = 3;

            while (resultsErrorCount <= maxResultsRetries) {
              try {
                resultsRes = await fetch(`${API_BASE}/audit/${taskId}/results`);

                if (!resultsRes.ok) {
                  const errData = await resultsRes.json().catch(() => ({}));
                  throw new Error(
                    errData.error ||
                      `Failed to get results (${resultsRes.status})`
                  );
                }

                resultsData = await resultsRes.json();
                break; // Success, exit retry loop
              } catch (resultsErr) {
                console.warn(
                  `[SEO Audit] Results fetch failed (attempt ${
                    resultsErrorCount + 1
                  }):`,
                  resultsErr
                );
                resultsErrorCount++;
                if (resultsErrorCount > maxResultsRetries) {
                  throw new Error(
                    `Failed to fetch results after ${maxResultsRetries} retries: ${resultsErr.message}`
                  );
                }
                // Wait 3 seconds before retrying
                await new Promise((resolve) => setTimeout(resolve, 3000));
              }
            }

            console.log('[SEO Audit] Results:', resultsData);

            const transformed = transformResults(resultsData);
            setAuditResults(transformed);

            // Cache the reportId per URL for persistence
            if (transformed.reportId && customUrl) {
              const cacheKey = `seo_audit_${customUrl.replace(
                /[^a-zA-Z0-9]/g,
                '_'
              )}`;
              localStorage.setItem(
                cacheKey,
                JSON.stringify({
                  reportId: transformed.reportId,
                  url: customUrl,
                  savedAt: new Date().toISOString()
                })
              );
              console.log('[SEO Audit] Cached report:', transformed.reportId);
            }

            setIsScanning(false);
            setScanPhase('');
          }
        } catch (pollErr) {
          console.warn('[SEO Audit] Polling temporary error:', pollErr);
          errorCount++;
          if (errorCount >= 4) {
            console.error('[SEO Audit] Polling failed after retries:', pollErr);
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setError(`Polling failed: ${pollErr.message}`);
            setIsScanning(false);
            setScanPhase('');
          }
        }
      }, 5000); // Poll every 5 seconds
    } catch (err) {
      console.error('[SEO Audit] Error:', err);
      setError(err.message);
      setIsScanning(false);
      setScanPhase('');
    }
  }, [customUrl, maxPages]);

  // ── Download report ──

  // ── Render ──

  // Show skeleton during initial loading
  if (isInitialLoading) {
    return <WebsiteSEOAuditSkeleton />;
  }

  return (
    <main className="p-6 lg:p-10 space-y-8">
      {/* Page Title */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-2">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
            Website SEO Audit
          </h1>
          <p className="text-base text-muted-foreground mt-1">
            Analyze and improve your website's search engine optimization GG
          </p>
        </div>
        <div className="flex items-center gap-4">
          {isSuperAdmin && (
            <div className="hidden lg:block relative w-72">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
              <ReactSelect
                placeholder="Search"
                onChange={(client) => handleChangeClient(client)}
                options={getCustomerList()}
                hideSelectedOptions={true}
                controlShouldRenderValue={false}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null
                }}
                openMenuOnClick={false}
                openMenuOnFocus={false}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: '48px',
                    height: '48px',
                    borderRadius: '0.75rem',
                    borderWidth: '1px',
                    borderColor: state.isFocused
                      ? 'hsl(var(--primary))'
                      : 'hsl(var(--border))',
                    boxShadow: state.isFocused
                      ? '0 0 0 1px hsl(var(--primary))'
                      : 'none',
                    backgroundColor: '#ffffff',
                    paddingLeft: '2.5rem',
                    fontSize: '1rem',
                    '&:hover': {
                      borderColor: 'hsl(var(--primary))'
                    }
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: 'hsl(var(--muted-foreground))',
                    fontSize: '1rem'
                  }),
                  input: (provided) => ({
                    ...provided,
                    fontSize: '1rem',
                    color: 'hsl(var(--foreground))'
                  }),
                  menu: (provided) => ({
                    ...provided,
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    boxShadow:
                      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
                    color: state.isFocused ? '#1e293b' : '#64748b',
                    cursor: 'pointer',
                    padding: '12px 16px',
                    fontSize: '1rem'
                  })
                }}
              />
            </div>
          )}
          <Link to="/seo-audit/settings">
            <Button variant="outline" className="gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </Button>
          </Link>
        </div>
      </div>

      <div className="space-y-6">
        {/* ── Intro / Trigger Card ── */}
        <Card className="shadow-md rounded-2xl border-0">
          <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
            <div className="flex items-center gap-4">
              <div
                className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(55, 102, 168, 0.1)' }}>
                <Search className="h-6 w-6" style={{ color: '#3766a8' }} />
              </div>
              <div className="flex-1">
                <CardTitle className="text-xl lg:text-2xl font-bold">
                  Analyze Your Website's SEO
                </CardTitle>
                <p className="text-base text-muted-foreground mt-1">
                  Enter any website URL to perform a comprehensive SEO audit
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-6 lg:px-8 pb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {/* Website URL input */}
              <div className="flex-1 w-full sm:w-auto relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground shrink-0 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Enter website URL (e.g., example.com)"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  className="h-12 pl-12 pr-12 text-base rounded-xl border-2 border-border bg-muted/50"
                  disabled={isScanning}
                />
                {customUrl && (
                  <a
                    href={
                      customUrl.startsWith('http')
                        ? customUrl
                        : `https://${customUrl}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>

              {/* Pages to crawl input */}
              <div className="w-full sm:w-auto flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={500}
                  value={maxPages}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val > 0) setMaxPages(Math.min(val, 500));
                  }}
                  disabled={isScanning}
                  className="h-12 w-24 text-center text-base rounded-xl border-2 border-border bg-muted/50"
                  placeholder="50"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  pages
                </span>
              </div>

              {/* Scan button */}
              <Button
                onClick={startAudit}
                disabled={isScanning || !customUrl.trim()}
                size="lg"
                className="h-14 px-8 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all gap-3"
                style={{
                  backgroundColor: '#3766a8',
                  color: '#fff',
                  border: 0
                }}>
                {isScanning ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Scanning...
                  </>
                ) : auditResults ? (
                  <>
                    <RefreshCw className="h-5 w-5" />
                    Re-Scan
                  </>
                ) : (
                  <>
                    <Search className="h-5 w-5" />
                    Run Audit
                  </>
                )}
              </Button>
            </div>

            {/* Scanning progress bar */}
            {isScanning && (
              <div className="mt-6 p-4 rounded-xl bg-muted/30 border border-border">
                <div className="flex items-center gap-3 mb-3">
                  <Loader2
                    className="h-5 w-5 animate-spin"
                    style={{ color: '#3766a8' }}
                  />
                  <span className="font-medium text-foreground">
                    {scanPhase === 'starting' && 'Starting audit...'}
                    {scanPhase === 'crawling' &&
                      `Crawling website... (${pagesCrawled} pages found)`}
                    {scanPhase === 'analyzing' && 'Analyzing results...'}
                  </span>
                </div>
                <Progress
                  value={
                    scanPhase === 'starting'
                      ? 10
                      : scanPhase === 'crawling'
                      ? Math.min(30 + pagesCrawled * 0.5, 80)
                      : scanPhase === 'analyzing'
                      ? 90
                      : 0
                  }
                  className="h-2"
                />
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="mt-4 p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  {error}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Results Section ── */}
        {auditResults && (
          <>
            {/* Score Overview */}
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  {/* Score circle */}
                  <div className="flex items-center gap-6">
                    <div
                      className={`flex flex-col items-center justify-center h-24 w-24 rounded-full border-4 ${getScoreBorderColor(
                        auditResults.score
                      )} bg-background`}>
                      <span
                        className={`text-3xl font-bold ${getScoreColor(
                          auditResults.score
                        )}`}>
                        {auditResults.score}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        / 100
                      </span>
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        SEO Score
                      </CardTitle>
                      <p className="flex flex-wrap items-center gap-2 mt-1 text-base text-muted-foreground">
                        <span className="truncate max-w-[240px] sm:max-w-none">
                          {auditResults.url}
                        </span>
                        {auditResults.crawlInfo && (
                          <Badge variant="secondary" className="gap-1 text-xs">
                            <Globe className="h-3 w-3" />
                            {auditResults.crawlInfo.pagesCrawled} pages
                          </Badge>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Report action buttons */}
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleDownloadReport}
                      className="h-12 px-6 text-base font-semibold rounded-xl border-2 transition-all gap-3"
                      style={{ borderColor: '#3766a8', color: '#3766a8' }}>
                      <Download className="h-5 w-5" />
                      Download Report
                    </Button>
                    {auditResults.reportId && (
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={() => {
                          const shareUrl = `${window.location.origin}/seo-report/${auditResults.reportId}`;
                          navigator.clipboard.writeText(shareUrl);
                          alert('Share link copied to clipboard!');
                        }}
                        className="h-12 px-6 text-base font-semibold rounded-xl border-2 transition-all gap-3"
                        style={{ borderColor: '#3766a8', color: '#3766a8' }}>
                        <ExternalLink className="h-5 w-5" />
                        Copy Share Link
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-6 lg:px-8 pb-6">
                <Progress
                  value={auditResults.score}
                  className="h-2 sm:h-3 mb-4"
                />
                <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
                  <div className="p-2 sm:p-3 rounded-lg bg-red-50 dark:bg-red-950">
                    <div className="text-xl sm:text-2xl font-bold text-red-600">
                      {auditResults.issues.critical}
                    </div>
                    <div className="text-[10px] sm:text-sm text-red-600 font-medium">
                      Critical
                    </div>
                  </div>
                  <div className="p-2 sm:p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950">
                    <div className="text-xl sm:text-2xl font-bold text-yellow-600">
                      {auditResults.issues.warnings}
                    </div>
                    <div className="text-[10px] sm:text-sm text-yellow-600 font-medium">
                      Warnings
                    </div>
                  </div>
                  <div className="p-2 sm:p-3 rounded-lg bg-green-50 dark:bg-green-950">
                    <div className="text-xl sm:text-2xl font-bold text-green-600">
                      {auditResults.issues.passed}
                    </div>
                    <div className="text-[10px] sm:text-sm text-green-600 font-medium">
                      Passed
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ─── SEO Health Checks (collapsible) ─── */}
            {auditResults.healthChecks && (
              <Card className="shadow-md rounded-2xl border-0">
                <CardHeader
                  className="pb-2 pt-6 px-6 lg:px-8 cursor-pointer select-none"
                  onClick={() => setHealthChecksOpen((o) => !o)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-green-50 flex items-center justify-center">
                        <Shield className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          SEO Health Checks
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {
                            auditResults.healthChecks.filter((c) => c.passed)
                              .length
                          }{' '}
                          of {auditResults.healthChecks.length} checks passed
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge
                        className={`text-xs px-2.5 py-1 ${
                          auditResults.healthChecks.every((c) => c.passed)
                            ? 'bg-green-100 text-green-700 hover:bg-green-100'
                            : 'bg-amber-100 text-amber-700 hover:bg-amber-100'
                        }`}>
                        {auditResults.healthChecks.every((c) => c.passed)
                          ? 'All Clear'
                          : 'Needs Attention'}
                      </Badge>
                      {healthChecksOpen ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </CardHeader>

                {healthChecksOpen && (
                  <CardContent className="px-6 lg:px-8 pb-6 pt-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {auditResults.healthChecks.map((check) => (
                        <div
                          key={check.key}
                          className={`flex items-start gap-3 p-3.5 rounded-xl border transition-colors ${
                            check.passed
                              ? 'border-green-200 bg-green-50/60'
                              : 'border-red-200 bg-red-50/60'
                          }`}>
                          <div className="mt-0.5 shrink-0">
                            {check.passed ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-500" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-sm font-semibold ${
                                  check.passed
                                    ? 'text-green-800'
                                    : 'text-red-800'
                                }`}>
                                {check.label}
                              </span>
                              <span
                                className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                                  check.passed
                                    ? 'bg-green-200 text-green-800'
                                    : 'bg-red-200 text-red-800'
                                }`}>
                                {check.passed ? 'PASS' : 'FAIL'}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                              <span className="font-medium">
                                Why it matters:
                              </span>{' '}
                              {check.why}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}

            {/* Google PageSpeed Insights (if PSI available) */}
            {(auditResults.psi?.mobile || auditResults.psi?.desktop) && (
              <Card className="shadow-md rounded-2xl border-0">
                <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className="h-12 w-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(55, 102, 168, 0.1)' }}>
                        <Zap className="h-6 w-6" style={{ color: '#3766a8' }} />
                      </div>
                      <div>
                        <CardTitle className="text-xl lg:text-2xl font-bold">
                          Google PageSpeed Insights
                        </CardTitle>
                        <p className="text-base text-muted-foreground mt-1">
                          Performance analysis by Google Lighthouse
                        </p>
                      </div>
                    </div>
                    {/* Mobile/Desktop Toggle */}
                    <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
                      <button
                        onClick={() => setPsiStrategy('mobile')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                          psiStrategy === 'mobile'
                            ? 'bg-background shadow-sm text-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}>
                        📱 Mobile
                      </button>
                      <button
                        onClick={() => setPsiStrategy('desktop')}
                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                          psiStrategy === 'desktop'
                            ? 'bg-background shadow-sm text-foreground'
                            : 'text-muted-foreground hover:text-foreground'
                        }`}>
                        🖥️ Desktop
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-6 lg:px-8 pb-6 space-y-6">
                  {/* Lighthouse Category Scores */}
                  {auditResults.psi[psiStrategy]?.lighthouseCategories ? (
                    <div>
                      <h4 className="font-semibold text-foreground mb-3">
                        Lighthouse Scores (
                        {psiStrategy === 'mobile' ? 'Mobile' : 'Desktop'})
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {Object.entries(
                          auditResults.psi[psiStrategy].lighthouseCategories
                        ).map(([key, val]) => (
                          <div
                            key={key}
                            className="p-4 rounded-xl border-2 border-border bg-background text-center">
                            <div className="text-xs font-medium text-muted-foreground mb-1 uppercase">
                              {key.replace(/([A-Z])/g, ' $1').trim()}
                            </div>
                            <div
                              className={`text-2xl font-bold ${getScoreColor(
                                Math.round(val * 100)
                              )}`}>
                              {Math.round(val * 100)}
                            </div>
                            <Progress
                              value={val * 100}
                              className="h-1.5 mt-2"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground italic">
                      {psiStrategy === 'mobile' ? 'Mobile' : 'Desktop'} PSI
                      scores not available.
                    </div>
                  )}

                  {/* Core Web Vitals */}
                  {auditResults.psi[psiStrategy]?.coreWebVitals && (
                    <div>
                      <h4 className="font-semibold text-foreground mb-3">
                        Core Web Vitals
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {Object.entries(
                          auditResults.psi[psiStrategy].coreWebVitals
                        ).map(([key, val]) => (
                          <div
                            key={key}
                            className="p-4 rounded-xl border-2 border-border bg-background text-center">
                            <div className="text-sm font-medium text-muted-foreground mb-1">
                              {key.toUpperCase()}
                            </div>
                            <div className="text-2xl font-bold text-foreground">
                              {String(val)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Performance Metrics */}
                  {auditResults.psi.metrics && (
                    <div>
                      <h4 className="font-semibold text-foreground mb-3">
                        Performance Metrics
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(auditResults.psi.metrics).map(
                          ([key, val]) => (
                            <div
                              key={key}
                              className="p-3 rounded-lg border border-border bg-muted/30">
                              <div className="text-xs font-medium text-muted-foreground mb-1">
                                {key
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (l) => l.toUpperCase())}
                              </div>
                              <div className="text-lg font-semibold text-foreground">
                                {typeof val === 'number'
                                  ? val > 1000
                                    ? `${(val / 1000).toFixed(2)}s`
                                    : val
                                  : String(val)}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Opportunities */}
                  {auditResults.psi.opportunities &&
                    auditResults.psi.opportunities.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-foreground mb-3">
                          Optimization Opportunities
                        </h4>
                        <div className="space-y-2">
                          {auditResults.psi.opportunities
                            .slice(0, 5)
                            .map((opp, idx) => (
                              <div
                                key={idx}
                                className="p-3 rounded-lg border border-border bg-muted/30 flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-foreground">
                                    {opp.title}
                                  </div>
                                  {opp.description && (
                                    <div className="text-xs text-muted-foreground mt-1">
                                      {opp.description}
                                    </div>
                                  )}
                                </div>
                                {opp.savings && (
                                  <Badge
                                    variant="outline"
                                    className="ml-2 text-green-600 border-green-300">
                                    Save {opp.savings}
                                  </Badge>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {/* Diagnostics */}
                  {auditResults.psi.diagnostics &&
                    auditResults.psi.diagnostics.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-foreground mb-3">
                          Diagnostics
                        </h4>
                        <div className="space-y-2">
                          {auditResults.psi.diagnostics
                            .slice(0, 5)
                            .map((diag, idx) => (
                              <div
                                key={idx}
                                className="p-3 rounded-lg border border-border bg-muted/30">
                                <div className="text-sm font-medium text-foreground">
                                  {diag.title}
                                </div>
                                {diag.description && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {diag.description}
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                </CardContent>
              </Card>
            )}

            {/* Category Breakdown */}
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                <CardTitle className="text-xl lg:text-2xl font-bold">
                  Category Breakdown
                </CardTitle>
                <p className="text-base text-muted-foreground mt-1">
                  Detailed analysis by SEO category
                </p>
              </CardHeader>
              <CardContent className="px-6 lg:px-8 pb-6">
                <div className="space-y-3 sm:space-y-4">
                  {auditResults.categories.map((category, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 sm:gap-4">
                      <div className="shrink-0">
                        {getStatusIcon(category.status)}
                      </div>
                      <div className="shrink-0 w-5">
                        {getCategoryIcon(category.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <span className="text-xs sm:text-sm font-medium truncate">
                            {category.name}
                          </span>
                          <span
                            className={`text-xs sm:text-sm font-semibold shrink-0 ${getScoreColor(
                              category.score
                            )}`}>
                            {category.score}%
                          </span>
                        </div>
                        <Progress
                          value={category.score}
                          className="h-1.5 sm:h-2"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Crawled Pages Section */}
            {auditResults.rawData?.pages &&
              auditResults.rawData.pages.length > 0 && (
                <Card className="shadow-md rounded-2xl border-0">
                  <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                    <div className="flex items-center gap-4">
                      <div
                        className="h-12 w-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: 'rgba(55, 102, 168, 0.1)' }}>
                        <FileSearch
                          className="h-6 w-6"
                          style={{ color: '#3766a8' }}
                        />
                      </div>
                      <div>
                        <CardTitle className="text-xl lg:text-2xl font-bold">
                          Crawled Pages
                        </CardTitle>
                        <p className="text-base text-muted-foreground mt-1">
                          {auditResults.rawData.pages.length} pages analyzed
                          with detailed SEO checks
                        </p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="px-6 lg:px-8 pb-8">
                    <div className="space-y-4">
                      {auditResults.rawData.pages.map((page, pageIndex) => {
                        const failedChecks = getFailedChecks(page.checks);
                        const passedChecks = getPassedChecks(page.checks);
                        const isExpanded = expandedPages[pageIndex];

                        return (
                          <div
                            key={pageIndex}
                            className="rounded-xl border-2 border-border bg-background overflow-hidden">
                            {/* Page Header */}
                            <button
                              onClick={() =>
                                setExpandedPages((prev) => ({
                                  ...prev,
                                  [pageIndex]: !prev[pageIndex]
                                }))
                              }
                              className="w-full p-4 flex items-center justify-between hover:bg-muted/30 transition-colors">
                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                <div
                                  className={`shrink-0 h-10 w-10 rounded-lg flex items-center justify-center ${
                                    page.statusCode === 200
                                      ? 'bg-green-100 dark:bg-green-950'
                                      : page.statusCode >= 400
                                      ? 'bg-red-100 dark:bg-red-950'
                                      : 'bg-yellow-100 dark:bg-yellow-950'
                                  }`}>
                                  {page.statusCode === 200 ? (
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                  ) : (
                                    <AlertCircle className="h-5 w-5 text-red-600" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0 text-left">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-foreground truncate">
                                      {page.url}
                                    </span>
                                    <Badge
                                      variant="outline"
                                      className="shrink-0 text-xs">
                                      {page.statusCode}
                                    </Badge>
                                    {page.onpageScore !== undefined && (
                                      <Badge
                                        className={`shrink-0 text-xs ${
                                          page.onpageScore >= 80
                                            ? 'bg-green-100 text-green-800'
                                            : page.onpageScore >= 50
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                        Score: {page.onpageScore.toFixed(0)}
                                      </Badge>
                                    )}
                                  </div>
                                  {page.meta?.title && (
                                    <p className="text-sm text-muted-foreground truncate mt-1">
                                      {page.meta.title}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 ml-4">
                                {failedChecks.length > 0 && (
                                  <Badge className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200">
                                    {failedChecks.length} issues
                                  </Badge>
                                )}
                                {isExpanded ? (
                                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                                )}
                              </div>
                            </button>

                            {/* Expanded Content */}
                            {isExpanded && (
                              <div className="border-t border-border p-4 space-y-4">
                                {/* Meta Information */}
                                {page.meta && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {page.meta.title && (
                                      <div className="p-3 rounded-lg bg-muted/30">
                                        <div className="text-xs font-medium text-muted-foreground mb-1">
                                          Page Title
                                        </div>
                                        <div className="text-sm font-medium text-foreground">
                                          {page.meta.title}
                                        </div>
                                      </div>
                                    )}
                                    {page.meta.description && (
                                      <div className="p-3 rounded-lg bg-muted/30">
                                        <div className="text-xs font-medium text-muted-foreground mb-1">
                                          Meta Description
                                        </div>
                                        <div className="text-sm text-foreground">
                                          {page.meta.description}
                                        </div>
                                      </div>
                                    )}
                                    {page.meta.h1 &&
                                      page.meta.h1.length > 0 && (
                                        <div className="p-3 rounded-lg bg-muted/30">
                                          <div className="text-xs font-medium text-muted-foreground mb-1">
                                            H1 Headings
                                          </div>
                                          <div className="text-sm text-foreground">
                                            {page.meta.h1.join(', ')}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                )}

                                {/* Failed Checks */}
                                {failedChecks.length > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                                      <XCircle className="h-4 w-4 text-red-500" />
                                      Issues Found ({failedChecks.length})
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                      {failedChecks.map((check, idx) => (
                                        <div
                                          key={idx}
                                          className={`p-3 rounded-lg border ${
                                            check.severity === 'critical'
                                              ? 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
                                              : check.severity === 'warning'
                                              ? 'bg-yellow-50 dark:bg-yellow-950/30 border-yellow-200 dark:border-yellow-800'
                                              : 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800'
                                          }`}>
                                          <div className="flex items-start gap-2">
                                            {check.severity === 'critical' ? (
                                              <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                                            ) : check.severity === 'warning' ? (
                                              <AlertTriangle className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
                                            ) : (
                                              <Info className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                                            )}
                                            <div>
                                              <div className="text-sm font-medium text-foreground">
                                                {check.label}
                                              </div>
                                              <div className="text-xs text-muted-foreground mt-0.5">
                                                {check.description}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Passed Checks */}
                                {passedChecks.length > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                      Passed Checks ({passedChecks.length})
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                      {passedChecks.map((check, idx) => (
                                        <div
                                          key={idx}
                                          className="p-3 rounded-lg border bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                                          <div className="flex items-start gap-2">
                                            <CheckCircle className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                                            <div>
                                              <div className="text-sm font-medium text-foreground">
                                                {check.label}
                                              </div>
                                              <div className="text-xs text-muted-foreground mt-0.5">
                                                {check.description}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Raw Checks Data */}
                                <details className="group">
                                  <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground flex items-center gap-2">
                                    <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                                    View raw checks data
                                  </summary>
                                  <pre className="mt-2 p-3 bg-muted/30 rounded-lg border border-border overflow-x-auto text-xs max-h-60">
                                    {JSON.stringify(page.checks, null, 2)}
                                  </pre>
                                </details>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

            {/* Recommendations */}
            {auditResults.recommendations.length > 0 && (
              <Card className="shadow-md rounded-2xl border-0">
                <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div
                      className="h-12 w-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(55, 102, 168, 0.1)' }}>
                      <CheckCircle
                        className="h-6 w-6"
                        style={{ color: '#3766a8' }}
                      />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        Recommendations
                      </CardTitle>
                      <p className="text-base text-muted-foreground mt-1">
                        {auditResults.recommendations.length} actionable items
                        to improve your SEO score
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-6 lg:px-8 pb-8">
                  {/* Severity Filter + Info */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <select
                        value={severityFilter}
                        onChange={(e) => setSeverityFilter(e.target.value)}
                        className="h-9 px-3 text-sm rounded-lg border border-border bg-muted/50 text-foreground appearance-none cursor-pointer"
                        style={{ minWidth: '130px' }}>
                        <option value="all">All Severity</option>
                        <option value="critical">Critical</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="warning">Warning</option>
                        <option value="low">Low</option>
                        <option value="info">Info</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Info className="h-3.5 w-3.5" />
                      <span>
                        Severity is based on SEO impact — Critical affects
                        indexing, High affects rankings
                      </span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {auditResults.recommendations
                      .filter(
                        (rec) =>
                          severityFilter === 'all' ||
                          rec.priority === severityFilter
                      )
                      .map((rec, index) => (
                        <div
                          key={index}
                          className="p-5 lg:p-6 rounded-2xl border-2 border-border bg-background hover:border-primary/30 transition-colors">
                          {/* Header row */}
                          <div className="flex items-start gap-4">
                            <div
                              className={`shrink-0 h-12 w-12 rounded-xl flex items-center justify-center ${
                                rec.priority === 'critical' ||
                                rec.priority === 'high'
                                  ? 'bg-red-100 dark:bg-red-950'
                                  : rec.priority === 'medium'
                                  ? 'bg-yellow-100 dark:bg-yellow-950'
                                  : 'bg-blue-100 dark:bg-blue-950'
                              }`}>
                              {getPriorityIcon(rec.priority)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                                <h4 className="font-semibold text-foreground text-lg leading-snug">
                                  {rec.title}
                                </h4>
                                <div className="flex items-center gap-2">
                                  <Badge
                                    className={`${getPriorityColor(
                                      rec.priority
                                    )} shrink-0 w-fit text-sm px-3 py-1`}
                                    variant="secondary">
                                    {getPriorityLabel(rec.priority)}
                                  </Badge>
                                  {rec.count > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="shrink-0 text-xs">
                                      {rec.count}{' '}
                                      {rec.count === 1 ? 'item' : 'items'}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <p className="text-base text-muted-foreground leading-relaxed">
                                {rec.description}
                              </p>

                              {/* View Items Toggle */}
                              {rec.items && rec.items.length > 0 && (
                                <button
                                  onClick={() =>
                                    setExpandedRecommendations((prev) => ({
                                      ...prev,
                                      [index]: !prev[index]
                                    }))
                                  }
                                  className="mt-2 text-sm font-medium text-primary hover:underline flex items-center gap-1">
                                  {expandedRecommendations[index]
                                    ? 'Hide'
                                    : 'View'}{' '}
                                  {rec.items.length} affected items
                                  {expandedRecommendations[index] ? (
                                    <ChevronUp className="h-3 w-3" />
                                  ) : (
                                    <ChevronDown className="h-3 w-3" />
                                  )}
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Expanded Items List */}
                          {expandedRecommendations[index] && rec.items && (
                            <div className="mt-4 p-4 bg-muted/30 rounded-xl border border-border">
                              <h5 className="text-sm font-semibold mb-2">
                                Affected Items:
                              </h5>
                              <ul className="space-y-1 max-h-60 overflow-y-auto">
                                {rec.items.map((item, i) => (
                                  <li
                                    key={i}
                                    className="text-sm text-muted-foreground flex items-center gap-2 truncate">
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0" />
                                    {item.url ? (
                                      <a
                                        href={item.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:underline truncate">
                                        {item.label || item.url}
                                      </a>
                                    ) : (
                                      <span className="truncate">
                                        {item.label}
                                      </span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* How to fix */}
                          {rec.fix && (
                            <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/30 rounded-xl border border-green-200 dark:border-green-800">
                              <p className="text-sm font-semibold text-green-700 dark:text-green-400 mb-2 flex items-center gap-2">
                                💡 How to fix
                              </p>
                              <p className="text-base text-green-600 dark:text-green-500 leading-relaxed">
                                {rec.fix}
                              </p>
                            </div>
                          )}

                          {/* Why it matters */}
                          {WHY_IT_MATTERS[rec.title] && (
                            <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-950/30 rounded-xl border border-blue-200 dark:border-blue-800">
                              <p className="text-sm font-semibold text-blue-700 dark:text-blue-400 mb-1 flex items-center gap-2">
                                📌 Why it matters
                              </p>
                              <p className="text-sm text-blue-600 dark:text-blue-500 leading-relaxed">
                                {WHY_IT_MATTERS[rec.title]}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Raw API Data Viewer */}
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
                <button
                  onClick={() => setShowRawData(!showRawData)}
                  className="flex items-center justify-between w-full text-left">
                  <div className="flex items-center gap-4">
                    <div
                      className="h-12 w-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(55, 102, 168, 0.1)' }}>
                      <Code className="h-6 w-6" style={{ color: '#3766a8' }} />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        Raw API Response
                      </CardTitle>
                      <p className="text-base text-muted-foreground mt-1">
                        View complete API response data
                      </p>
                    </div>
                  </div>
                  {showRawData ? (
                    <ChevronUp className="h-6 w-6 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-6 w-6 text-muted-foreground" />
                  )}
                </button>
              </CardHeader>
              {showRawData && (
                <CardContent className="px-6 lg:px-8 pb-8">
                  <div className="space-y-6">
                    {/* Raw Scores */}
                    {auditResults.rawScores && (
                      <div>
                        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Zap
                            className="h-4 w-4"
                            style={{ color: '#3766a8' }}
                          />
                          Scores Object
                        </h4>
                        <pre className="p-4 bg-muted/30 rounded-xl border border-border overflow-x-auto text-sm">
                          {JSON.stringify(auditResults.rawScores, null, 2)}
                        </pre>
                      </div>
                    )}

                    {/* Raw Issues */}
                    {auditResults.rawIssues && (
                      <div>
                        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          Issues Array ({auditResults.rawIssues.length} items)
                        </h4>
                        <pre className="p-4 bg-muted/30 rounded-xl border border-border overflow-x-auto text-sm max-h-96">
                          {JSON.stringify(auditResults.rawIssues, null, 2)}
                        </pre>
                      </div>
                    )}

                    {/* PSI Data */}
                    {auditResults.psi && (
                      <div>
                        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Monitor
                            className="h-4 w-4"
                            style={{ color: '#3766a8' }}
                          />
                          PageSpeed Insights
                        </h4>
                        <pre className="p-4 bg-muted/30 rounded-xl border border-border overflow-x-auto text-sm">
                          {JSON.stringify(auditResults.psi, null, 2)}
                        </pre>
                      </div>
                    )}

                    {/* Crawl Info */}
                    {auditResults.crawlInfo && (
                      <div>
                        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Globe
                            className="h-4 w-4"
                            style={{ color: '#3766a8' }}
                          />
                          Crawl Info
                        </h4>
                        <pre className="p-4 bg-muted/30 rounded-xl border border-border overflow-x-auto text-sm">
                          {JSON.stringify(auditResults.crawlInfo, null, 2)}
                        </pre>
                      </div>
                    )}

                    {/* Other Raw Data */}
                    {Object.keys(auditResults.rawData).length > 0 && (
                      <div>
                        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <FileText
                            className="h-4 w-4"
                            style={{ color: '#3766a8' }}
                          />
                          Additional Data
                        </h4>
                        <pre className="p-4 bg-muted/30 rounded-xl border border-border overflow-x-auto text-sm">
                          {JSON.stringify(auditResults.rawData, null, 2)}
                        </pre>
                      </div>
                    )}

                    {/* Full Response */}
                    <div>
                      <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Code
                          className="h-4 w-4"
                          style={{ color: '#3766a8' }}
                        />
                        Complete Response
                      </h4>
                      <pre className="p-4 bg-muted/30 rounded-xl border border-border overflow-x-auto text-sm max-h-[600px]">
                        {JSON.stringify(auditResults, null, 2)}
                      </pre>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          </>
        )}
      </div>
      <div
        style={{
          position: 'absolute',
          top: -10000,
          left: -10000,
          width: '1200px'
        }}>
        <SEOReportTemplate
          ref={reportRef}
          data={auditResults}
          url={websiteUrl}
        />
      </div>
    </main>
  );
};

export default WebsiteSEOAudit;
